/**
 * Workspaces Search Screen (Public/Unauthenticated)
 *
 * This screen uses the SpacesSearchView component in unauthenticated mode
 * to display public workspace listings for visitors. Tapping a space opens
 * the reusable booking modal.
 */

import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import type { SpacesSearchViewProps } from "../../types/components";
import SpacesSearchViewImport from "../../components/SpacesSearchView";
import ReusableBookingModal from "../../components/ReusableBookingModal";
import { ComponentType } from "react";
import SEO from "../../components/SEO/SEO";
import * as localService from "../../services/localService";
import { spaceManagementService } from "../../services/host-apiServices/spaceManagementService";
import {
  readPendingBookingSelectionForSpace,
  clearPendingBookingSelection,
} from "../../utils/pendingBookingSelection";
import logger from "../../utils/logger";

const SpacesSearchView = SpacesSearchViewImport as ComponentType<SpacesSearchViewProps>;

const WorkspacesSearch: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [selectedSpace, setSelectedSpace] = useState<any>(null);
  const [restoredSelection, setRestoredSelection] = useState<any>(null);

  const initialFilters = React.useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const filters: Record<string, any> = {};

    const hostOrganizationId = queryParams.get("hostOrganizationId");
    if (hostOrganizationId) {
      const parsed = Number(hostOrganizationId);
      if (Number.isInteger(parsed) && parsed > 0) {
        filters.hostOrganizationId = parsed;
      }
    }
    const hostOrgName = queryParams.get("hostOrgName");
    if (hostOrgName) {
      filters.hostOrgName = hostOrgName;
    }

    const subCategoryId = queryParams.get("subCategoryId");
    if (subCategoryId) {
      const parsedSub = Number(subCategoryId);
      if (Number.isInteger(parsedSub) && parsedSub > 0) {
        filters.subCategoryId = parsedSub;
      }
    }

    const categoryId = queryParams.get("categoryId");
    if (categoryId) {
      const parsedCat = Number(categoryId);
      if (Number.isInteger(parsedCat) && parsedCat > 0) {
        filters.categoryId = parsedCat;
      }
    }

    const type = queryParams.get("type");
    if (type) {
      const typeCategoryMap: Record<string, number> = { work: 1, stay: 2, play: 3, eat: 4 };
      if (typeCategoryMap[type.toLowerCase()]) {
        filters.categoryId = typeCategoryMap[type.toLowerCase()];
      }
    }

    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    if (startDate && endDate) {
      filters.startDate = startDate;
      filters.endDate = endDate;
    }

    const specificDate = queryParams.get("specificDate");
    if (specificDate) {
      filters.specificDate = specificDate.split(",").map(d => d.trim()).filter(Boolean);
    }

    const searchQuery = queryParams.get("query") || queryParams.get("addressContains") || queryParams.get("search");
    if (searchQuery) {
      filters.addressContains = searchQuery;
    }

    const spaceName = queryParams.get("spaceName");
    if (spaceName) {
      filters.spaceName = spaceName;
    }

    const country = queryParams.get("country");
    if (country) {
      filters.country = country;
    }

    const buildingName = queryParams.get("buildingName");
    if (buildingName) {
      filters.buildingName = buildingName;
    }

    const cityOrTown = queryParams.get("cityOrTown") || queryParams.get("city");
    if (cityOrTown) {
      filters.cityOrTown = cityOrTown;
    }

    const filterOptionIds = queryParams.get("filterOptionIds");
    if (filterOptionIds) {
      filters.filterOptionIds = filterOptionIds;
    }

    const dropoffLocation = queryParams.get("dropoffLocation");
    if (dropoffLocation) {
      filters.dropoffLocation = dropoffLocation;
    }

    return filters;
  }, [location.search]);

  const handleSpaceClick = (space: any, updateUrl = true) => {
    if (!space) return;

    if (updateUrl && space.id) {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get("spaceId") !== String(space.id)) {
        searchParams.set("spaceId", String(space.id));
        history.push({ search: searchParams.toString() });
      }
    }

    const pending = readPendingBookingSelectionForSpace(Number(space.id));
    if (pending) {
      setRestoredSelection(pending);
    }

    setSelectedSpace(space);
  };

  const handleCloseModal = () => {
    setSelectedSpace(null);
    setRestoredSelection(null);

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("spaceId") || searchParams.has("space")) {
      searchParams.delete("spaceId");
      searchParams.delete("space");
      history.push({ search: searchParams.toString() });
    }
  };

  // Listen for spaceId in URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const spaceIdParam = searchParams.get("spaceId") || searchParams.get("space");
    if (spaceIdParam) {
      const parsedId = Number(spaceIdParam);
      if (!Number.isNaN(parsedId)) {
        if (selectedSpace?.id === parsedId) return;

        const pending = readPendingBookingSelectionForSpace(parsedId);
        if (pending) {
          setRestoredSelection(pending);
        }

        spaceManagementService
          .getPublicSpaceById(parsedId)
          .catch(() => spaceManagementService.getSpaceById(parsedId))
          .then(response => {
            const data = response?.data?.data || response?.data;
            if (data) {
              handleSpaceClick(data, false);
            }
          })
          .catch(error => {
            logger.error("Error loading space in workspaces search:", error);
          });
      }
    }
  }, [location.search]);

  const user = localService.getUser();

  return (
    <>
      <SEO
        title="Search Workspaces & Spaces | Spacia - Find Your Perfect Space"
        description="Search and discover flexible workspaces, coworking spaces, accommodations, and experiences across Africa. Filter by location, price, amenities, and more."
        keywords={[
          "spacia search",
          "workspace search",
          "coworking spaces",
          "find workspace",
          "space booking",
          "africa workspaces",
        ]}
        url={typeof window !== "undefined" ? `${window.location.origin}/spaces` : ""}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Search Workspaces on Spacia",
          description: "Search and discover flexible workspaces, coworking spaces, and accommodations across Africa",
          url: typeof window !== "undefined" ? `${window.location.origin}/spaces` : "",
        }}
      />
      <SpacesSearchView
        mode={user ? "authenticated" : "unauthenticated"}
        showBackButton={true}
        title="Search"
        subtitle="Discover amazing spaces for your next project"
        pageSize={10}
        initialFilters={initialFilters}
        onSpaceClick={handleSpaceClick}
      />

      {selectedSpace && (
        <ReusableBookingModal
          isOpen={Boolean(selectedSpace)}
          onClose={handleCloseModal}
          space={selectedSpace}
          initialSelection={restoredSelection || undefined}
          selectedDate={new Date()}
          timeFormat="12h"
          closeOnSuccess={true}
          onBookingSuccess={() => {
            clearPendingBookingSelection();
          }}
        />
      )}
    </>
  );
};

export default WorkspacesSearch;
